import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './privacy-policy.html',
  styleUrls: ['./privacy-policy.css']
})
export class PrivacyPolicyComponent {
  lastUpdated = 'November 27, 2025';

  sections = [
    {
      id: 'information-collection',
      title: 'Information Collection',
      content: `
        <h3>1.1 Information You Provide</h3>
        <p>We collect information that you provide directly to us, including:</p>
        <ul>
          <li>Name, email address, and contact information when you create an account or contact us</li>
          <li>Professional information such as company name, job title, and business details</li>
          <li>Payment and billing information when you purchase our services</li>
          <li>Communications with us, including support requests and feedback</li>
          <li>Project requirements, specifications, and related documents</li>
        </ul>

        <h3>1.2 Information We Collect Automatically</h3>
        <p>When you use our services, we automatically collect certain information, including:</p>
        <ul>
          <li>Device information (IP address, browser type, operating system)</li>
          <li>Usage data (pages visited, time spent, features used)</li>
          <li>Log data (access times, error logs, performance metrics)</li>
          <li>Location information (derived from IP address)</li>
        </ul>

        <h3>1.3 Information from Third Parties</h3>
        <p>We may receive information from third-party services you connect to our platform, such as authentication providers, payment processors, and business tools.</p>
      `
    },
    {
      id: 'use-of-information',
      title: 'Use of Information',
      content: `
        <p>We use the collected information for the following purposes:</p>
        <ul>
          <li><strong>Service Delivery:</strong> To provide, maintain, and improve our technology services and solutions</li>
          <li><strong>Communication:</strong> To respond to your inquiries, send service updates, and provide customer support</li>
          <li><strong>Personalization:</strong> To customize your experience and provide relevant content and recommendations</li>
          <li><strong>Analytics:</strong> To understand how our services are used and improve functionality</li>
          <li><strong>Security:</strong> To protect against fraud, unauthorized access, and other security risks</li>
          <li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms of service</li>
          <li><strong>Marketing:</strong> To send promotional materials and updates about our services (with your consent)</li>
          <li><strong>Development:</strong> To develop new features, products, and services</li>
        </ul>
      `
    },
    {
      id: 'data-sharing',
      title: 'Data Sharing',
      content: `
        <h3>3.1 When We Share Information</h3>
        <p>We may share your information in the following circumstances:</p>
        <ul>
          <li><strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our business (hosting, payment processing, analytics)</li>
          <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or sale of company assets</li>
          <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
          <li><strong>Protection of Rights:</strong> To protect our rights, property, safety, or that of our users</li>
          <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
        </ul>

        <h3>3.2 What We Don't Do</h3>
        <p>We do not:</p>
        <ul>
          <li>Sell your personal information to third parties</li>
          <li>Share your data with advertisers without your consent</li>
          <li>Use your confidential project information for any purpose other than providing services to you</li>
        </ul>
      `
    },
    {
      id: 'security',
      title: 'Security',
      content: `
        <p>We take the security of your information seriously and implement appropriate technical and organizational measures to protect it:</p>
        <ul>
          <li><strong>Encryption:</strong> Data is encrypted in transit using SSL/TLS and at rest using industry-standard encryption</li>
          <li><strong>Access Controls:</strong> Strict access controls ensure only authorized personnel can access your data</li>
          <li><strong>Regular Audits:</strong> We conduct regular security audits and vulnerability assessments</li>
          <li><strong>Secure Infrastructure:</strong> Our services are hosted on secure, enterprise-grade infrastructure</li>
          <li><strong>Employee Training:</strong> Our team is trained on data protection and security best practices</li>
          <li><strong>Incident Response:</strong> We maintain procedures for detecting and responding to security incidents</li>
        </ul>
        <p>However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee its absolute security.</p>
      `
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking Technologies',
      content: `
        <h3>5.1 What We Use</h3>
        <p>We use cookies and similar tracking technologies to enhance your experience:</p>
        <ul>
          <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
          <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
          <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
          <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with your consent)</li>
        </ul>

        <h3>5.2 Your Choices</h3>
        <p>You can control cookies through your browser settings. Note that disabling certain cookies may impact your ability to use some features of our services.</p>

        <h3>5.3 Third-Party Analytics</h3>
        <p>We may use third-party analytics services such as Google Analytics to collect and analyze usage information. These services have their own privacy policies governing their use of information.</p>
      `
    },
    {
      id: 'your-rights',
      title: 'Your Rights',
      content: `
        <p>Depending on your location, you may have certain rights regarding your personal information:</p>
        <ul>
          <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
          <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
          <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
          <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
          <li><strong>Objection:</strong> Object to certain processing activities</li>
          <li><strong>Restriction:</strong> Request restriction of processing under certain circumstances</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent for processing based on consent</li>
          <li><strong>Opt-Out:</strong> Opt-out of marketing communications at any time</li>
        </ul>

        <h3>6.1 How to Exercise Your Rights</h3>
        <p>To exercise any of these rights, please contact us at <a href="mailto:privacy@honeykode.com">privacy@honeykode.com</a>. We will respond to your request within 30 days.</p>

        <h3>6.2 Data Retention</h3>
        <p>We retain your information for as long as necessary to provide our services and comply with legal obligations. When information is no longer needed, we securely delete or anonymize it.</p>
      `
    },
    {
      id: 'contact',
      title: 'Contact Information',
      content: `
        <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
        <div class="contact-info">
          <p><strong>HoneyKode</strong></p>
          <p>Email: <a href="mailto:privacy@honeykode.com">privacy@honeykode.com</a></p>
          <p>General Inquiries: <a href="mailto:info@honeykode.com">info@honeykode.com</a></p>
          <p>Phone: +1 (555) 123-4567</p>
        </div>

        <h3>7.1 Changes to This Policy</h3>
        <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.</p>

        <h3>7.2 Children's Privacy</h3>
        <p>Our services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.</p>

        <h3>7.3 International Data Transfers</h3>
        <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.</p>
      `
    }
  ];
}
